import axios, { type AxiosRequestConfig } from "axios";
import { useAuthStore } from "@/frontend/stores";
const BASE_URL =
  typeof window !== "undefined"
    ? window.location.hostname === "localhost"
      ? `${window.location.protocol}//${window.location.hostname}:4001`
      : `${window.location.origin}/api`
    : "http://localhost:4001";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();

    for (const key in params) {
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    }

    return searchParams.toString();
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();

    if (config.headers.Authorization) {
      return config;
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const store = useAuthStore.getState();
        const { refreshToken } = store;

        if (refreshToken) {
          const { data } = await axios.post(`${BASE_URL}/auth/public/refresh`, {
            refreshToken,
          });
          const {
            accessToken,
            refreshToken: newRefreshToken,
            user,
            permissions,
          } = data.data!;

          store.updateTokens(newRefreshToken, accessToken);
          store.updateUser(user);
          store.updatePermissions(permissions);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          if (
            originalRequest.url?.includes("/logout") &&
            originalRequest.data
          ) {
            try {
              const body =
                typeof originalRequest.data === "string"
                  ? JSON.parse(originalRequest.data)
                  : originalRequest.data;

              if (body.refreshToken) {
                body.refreshToken = newRefreshToken;
                originalRequest.data = JSON.stringify(body);
              }
            } catch (e) {
              console.warn("No se pudo actualizar el body del request:", e);
            }
          }
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error("Error al refrescar token:", refreshError);
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 403) {
      console.error("No tienes permisos para realizar esta acción");
    }

    return Promise.reject(error);
  },
);

export const api = {
  get: <T>(endpoint: string, options?: AxiosRequestConfig) =>
    axiosInstance
      .get<T>(endpoint, { ...options })
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),
  getWithParams: <T>(
    endpoint: string,
    params?: Record<string, any>,
    options?: AxiosRequestConfig,
  ) =>
    axiosInstance
      .get<T>(endpoint, { ...options, params })
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),
  post: <T>(endpoint: string, body?: unknown, options?: AxiosRequestConfig) =>
    axiosInstance
      .post<T>(endpoint, body, options)
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),
  put: <T>(endpoint: string, body?: unknown, options?: AxiosRequestConfig) =>
    axiosInstance
      .put<T>(endpoint, body, options)
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),

  patch: <T>(endpoint: string, body?: unknown, options?: AxiosRequestConfig) =>
    axiosInstance.patch<T>(endpoint, body, options).then((res) => res.data)
      .catch((err) => {
        return err.response.data;
      }),

  delete: <T>(endpoint: string, options?: AxiosRequestConfig) =>
    axiosInstance
      .delete<T>(endpoint, { ...options })
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),
};

const axiosPublicInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const apiPublic = {
  get: <T>(endpoint: string, options?: AxiosRequestConfig) =>
    axiosPublicInstance
      .get<T>(endpoint, { ...options })
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),
  post: <T>(endpoint: string, body?: unknown, options?: AxiosRequestConfig) =>
    axiosPublicInstance
      .post<T>(endpoint, body, options)
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.data) return err.response.data;
        throw err;
      }),
};
